import {
    Component,
    ojs,
    app,
    state,
    EventData,
    eData,
    v,
} from "modular-openscriptjs";
import { dom, tool } from "../../fotastart";
import {
    oneOf,
    either,
    each,
    cleanDispose,
    isMobile,
    inform,
} from "../../helpers";
import { STRINGS } from "../../declarations";
import CheckYesno from "../Blocks/CheckYesno";

const h = app("h");
const broker = app("broker");
const $e = app("$e");

// Pre-filled reasons the customer can pick from when the delivery
// experience was rated poorly. Kept short and specific on purpose -
// the goal is a quick tap, not another form to fill in.
const REVIEW_REASONS = [
    "Late delivery",
    "Long processing time",
    "Poor behaviour from the delivery person",
    "Order took longer than expected",
    "Poor communication",
];

export default class ReviewForm extends Component {
    rating = state(0);
    holder = null;

    async showForm(ed, event) {
        let data = EventData.parse(ed);
        const review = data?.message.get("review");
        const orderId = data?.message.get("orderId", 0);

        this.rating.value = oneOf(review?.rating, 0);

        if (this.holder) {
            cleanDispose(this.holder);
        }

        if (isMobile()) {
            this.holder = tool.offcanvas(
                [
                    h.ReviewCanvas(this.renderForm(review)),
                    h.div(
                        {
                            class: "offcanvas-footer p-3",
                            style: "flex-shrink: 0;",
                        },
                        this.renderFooter(review, orderId),
                    ),
                ],
                { backdrop: true },
            );
        } else {
            this.holder = tool.plainModal(
                h.ReviewModal(
                    this.renderForm(review),
                    this.renderFooter(review, orderId),
                ),
                { backdrop: true },
            );
        }
        this.holder.show();
    }

    closeForm() {
        if (this.holder) {
            this.holder.hide();
        }
    }

    rate(rating) {
        this.rating.value = rating;
    }

    // Reveals (or hides) the free-text box when the "Other" option is
    // toggled. The box otherwise stays out of the way so the form
    // doesn't look complicated.
    toggleOtherInput(elem) {
        let input = dom.id("review-input-review");
        if (!input) return;

        elem.checked
            ? input.classList.remove("d-none")
            : input.classList.add("d-none");

        if (elem.checked) input.focus();
    }

    reasonCheckbox(label) {
        let uniqueId = `review-reason-${label
            .replace(/\s+/g, "-")
            .replace(/[^a-zA-Z-]/g, "")
            .toLowerCase()}-${Math.floor(Math.random() * 1000)}`;

        return h.div(
            {
                class: "d-flex justify-content-between align-items-center p-2 px-3 border rounded-3 bg-white shadow-sm border-light-subtle",
            },
            h.span({ class: "small fw-semibold text-dark" }, label),
            h.CheckYesno({
                name: "review-reason",
                data_reason: label,
                id: uniqueId,
                uid: uniqueId,
                label: { value: "" },
            }),
        );
    }

    // Pre-filled reasons + free-text box, shown only for a poor rating
    // (3 stars and below). A good rating doesn't need to justify itself.
    renderReasons(rating, review) {
        if (rating < 1 || rating > 3) {
            return h.empty();
        }

        // Editing a review that already has text: reveal the box and
        // restore what was typed, since it may not match any of the
        // pre-filled reasons below.
        const existingText = oneOf(review?.review, "");
        const hasExistingText = existingText.length > 0;

        return h.div(
            { class: "mb-3" },
            h.label(
                { class: "form-label fw-bold small text-secondary mb-2" },
                "What went wrong?",
            ),
            h.div(
                { class: "d-flex flex-column gap-2 mb-3" },
                each(REVIEW_REASONS, (reason) => this.reasonCheckbox(reason)),

                // Other - reveals the text box below for a custom reason
                h.div(
                    {
                        class: "d-flex justify-content-between align-items-center p-2 px-3 border rounded-3 bg-white shadow-sm border-light-subtle",
                    },
                    h.span({ class: "small fw-semibold text-dark" }, "Other"),
                    h.CheckYesno({
                        name: "review-reason",
                        id: "review-reason-other",
                        uid: "review-reason-other",
                        label: { value: "" },
                        checked: hasExistingText,
                        onchange: this.method("toggleOtherInput", "${this}"),
                    }),
                ),
            ),

            h.textarea(
                {
                    class: `form-control bg-light border-0 p-3 rounded-3 ${hasExistingText ? "" : "d-none"}`,
                    placeholder: "Tell us what happened...",
                    id: "review-input-review",
                    rows: "3",
                },
                existingText,
            ),
        );
    }

    submitReview(reviewId, orderId) {
        let reasons = [];
        let reasonsList = dom.all("[name='review-reason']");

        for (let r of reasonsList) {
            if (!r.checked) continue;

            if (r.id == "review-reason-other") {
                let other = dom.id("review-input-review");
                let value = (other?.value || "").trim();

                if (!value) {
                    inform("Please describe the issue", STRINGS.WARNING);
                    return;
                }

                reasons.push(value);
                continue;
            }

            reasons.push(r.dataset.reason);
        }

        let btn = dom.id("confirm-review-btn");
        dom.disable(btn);
        dom.put("Saving...", btn);

        broker.send(
            $e.review.needs.storage,
            eData(
                {},
                {
                    id: reviewId,
                    title: dom.id("review-input-title")?.value || "",
                    rating: this.rating.value,
                    review: reasons.join(". "),
                    orderId,
                },
            ),
        );
    }

    renderForm(review) {
        return h.div(
            { class: "w-100 text-start" },
            // Header
            h.div(
                {
                    class: "d-flex justify-content-between align-items-start mb-4",
                },
                h.div(
                    h.h4({ class: "fw-bold text-dark mb-1" }, "Review Delivery"),
                    // Make it unmistakable that this rates the deliverer/
                    // delivery service, not the businesses in the order -
                    // those get rated separately.
                    h.span(
                        {
                            class: "badge bg-primary-subtle text-primary rounded-pill fw-semibold",
                            style: "font-size: 0.7rem;",
                        },
                        h.i({ class: "fas fa-truck me-1" }),
                        "Delivery only — not the businesses",
                    ),
                ),
                h.button({
                    class: "btn-close bg-light rounded-circle p-2 flex-shrink-0",
                    onclick: this.method("closeForm"),
                    aria_label: "Close",
                }),
            ),

            // Hidden Review ID
            h.input({
                type: "hidden",
                id: "review-input-id",
                value: oneOf(review?.id, 0),
            }),

            // Stars
            h.label(
                { class: "form-label fw-bold small text-secondary mb-2" },
                "Your Rating",
            ),
            v(this.rating, (rating) => {
                return h.div(
                    { class: "d-flex gap-2 mb-4" },
                    each([1, 2, 3, 4, 5], (r) =>
                        h.span(
                            {
                                class: "fs-2 cursor-pointer transition-all",
                                onclick: this.method("rate", r),
                                style: "cursor: pointer;",
                            },
                            h.i({
                                class: either(
                                    r <= rating.value,
                                    "fas fa-star text-warning",
                                    "far fa-star text-secondary-subtle",
                                ),
                            }),
                        ),
                    ),
                );
            }),

            // Reasons + review text - only for a poor rating (3 and below)
            v(this.rating, (rating) => this.renderReasons(rating.value, review)),

            // Hidden Title Input
            h.input({
                type: "hidden",
                id: "review-input-title",
                value: oneOf(review?.title, ""),
            }),
        );
    }

    renderFooter(review, orderId) {
        let reviewId = oneOf(review?.id, 0);
        return h.div(
            {
                class: "d-flex justify-content-between align-items-center gap-3 w-100",
            },
            h.button(
                {
                    class: "btn btn-light rounded-pill px-4 py-3 fw-bold flex-fill",
                    onclick: this.method("closeForm"),
                },
                "Cancel",
            ),
            h.button(
                {
                    class: "btn btn-primary rounded-pill px-4 py-3 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2 flex-fill",
                    id: "confirm-review-btn",
                    onclick: this.method("submitReview", [reviewId, orderId]),
                },
                h.span("Submit"),
                h.i({ class: "fas fa-paper-plane" }),
            ),
        );
    }

    $$review = {
        stored: (ed, event) => {
            if (this.holder) {
                this.holder.hide();
            }
        },

        needs: {
            reviewForm: (ed, event) => this.showForm(ed, event),
        },

        has: {
            storageError: (ed, event) => {
                let btn = dom.id("confirm-review-btn");
                if (btn) {
                    dom.enable(btn);
                    dom.put(h.span("Submit"), btn);
                    dom.add(h.i({ class: "fas fa-paper-plane" }), btn);
                }
            },
        },
    };

    cleanUp() {
        super.cleanUp();
        if (this.holder) cleanDispose(this.holder);
    }
}

export function ReviewCanvas(bodyContent) {
    return h.$(
        h.div(
            {
                class: "offcanvas-body p-4",
                style: "flex-grow: 1; overflow-y: auto;",
            },
            bodyContent,
        ),
    );
}

export function ReviewModal(bodyContent, footerContent) {
    return h.$(
        h.div(
            {
                class: "modal-content border-0 rounded-4 shadow-lg",
            },
            h.div(
                {
                    class: "modal-body p-4",
                },
                bodyContent,
                h.div(
                    {
                        class: "modal-footer p-4",
                    },
                    footerContent,
                ),
            ),
        ),
    );
}

ojs(ReviewForm, ReviewCanvas, ReviewModal);
